import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  MapPin, Bed, Bath, Users, Heart, Share2, Star, Zap, CheckCircle, 
  MessageSquare, Calendar, ChevronLeft, ChevronRight, Shield, Loader2,
  Flag, AlertTriangle
} from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Layout from '../components/Layout';
import { propertiesApi, bookingsApi, messagesApi, savedApi, ownerPolicyApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format, addDays, differenceInDays } from 'date-fns';

// Fix Leaflet marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  
  // Booking state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [guests, setGuests] = useState(1);
  const [bookingMessage, setBookingMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Message state
  const [messageContent, setMessageContent] = useState('');

  // Report state
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const [propRes, reviewsRes] = await Promise.all([
        propertiesApi.getById(id),
        propertiesApi.getReviews(id)
      ]);
      setProperty(propRes.data);
      setReviews(reviewsRes.data);

      if (isAuthenticated) {
        const savedRes = await savedApi.check(id);
        setIsSaved(savedRes.data.saved);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Property not found');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save properties');
      return;
    }
    try {
      const response = await savedApi.toggle(id);
      setIsSaved(response.data.saved);
      toast.success(response.data.saved ? 'Property saved' : 'Property unsaved');
    } catch (error) {
      toast.error('Failed to save property');
    }
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to book');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select dates');
      return;
    }
    
    setSubmitting(true);
    try {
      await bookingsApi.create({
        property_id: id,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        guests,
        message: bookingMessage
      });
      toast.success(property.instant_booking ? 'Booking confirmed!' : 'Booking request sent!');
      setBookingDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to send messages');
      return;
    }
    if (!messageContent.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await messagesApi.send({
        recipient_id: property.owner_id,
        property_id: id,
        content: messageContent
      });
      toast.success('Message sent!');
      setMessageDialogOpen(false);
      setMessageContent('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportListing = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to report listings');
      return;
    }
    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }

    setReportSubmitting(true);
    try {
      await ownerPolicyApi.reportListing(id, reportReason, reportDetails || null);
      toast.success('Thank you for your report. Our team will review this listing.');
      setReportDialogOpen(false);
      setReportReason('');
      setReportDetails('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('already reported')) {
        toast.error('You have already reported this listing');
      } else {
        toast.error('Failed to submit report');
      }
    } finally {
      setReportSubmitting(false);
    }
  };

  const calculateTotal = () => {
    if (!startDate || !endDate || !property) return 0;
    const days = differenceInDays(endDate, startDate);
    const months = days / 30;
    return (property.price_per_month * months) + property.deposit_amount;
  };

  if (loading) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  if (!property) return null;

  const photos = property.photos?.length > 0 ? property.photos : [
    'https://images.unsplash.com/photo-1597665863042-47e00964d899?w=800'
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Photo Gallery */}
        <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden mb-8 bg-slate-100">
          <img
            src={photos[currentImageIndex]}
            alt={property.title}
            className="w-full h-full object-cover"
          />
          
          {photos.length > 1 && (
            <>
              <button
                onClick={() => setCurrentImageIndex((prev) => prev === 0 ? photos.length - 1 : prev - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="prev-image"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => setCurrentImageIndex((prev) => prev === photos.length - 1 ? 0 : prev + 1)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                data-testid="next-image"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {photos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              onClick={handleSave}
              className="p-3 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
              data-testid="save-property"
            >
              <Heart className={`w-5 h-5 ${isSaved ? 'fill-coral text-coral' : 'text-slate-600'}`} />
            </button>
            <button className="p-3 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors">
              <Share2 className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge className="bg-soft-gray text-slate-700">{property.property_type}</Badge>
                {property.instant_booking && (
                  <Badge className="bg-trust-blue text-white">
                    <Zap className="w-3 h-3 mr-1" />
                    Instant Book
                  </Badge>
                )}
              </div>
              
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="property-title">
                {property.title}
              </h1>
              
              <div className="flex items-center gap-4 text-slate-600">
                <div className="flex items-center gap-1">
                  <MapPin className="w-5 h-5" />
                  {property.address}, {property.city}
                </div>
                {property.average_rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    <span className="font-medium">{property.average_rating.toFixed(1)}</span>
                    <span className="text-slate-400">({property.review_count} reviews)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-6 py-6 border-y border-slate-200">
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-slate-400" />
                <span>{property.bedrooms} {property.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-slate-400" />
                <span>{property.bathrooms} {property.bathrooms === 1 ? 'Bathroom' : 'Bathrooms'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" />
                <span>Up to {property.max_guests} guests</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <span>Min {property.min_stay_days} days</span>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">About this place</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {property.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      {amenity}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* House Rules */}
            {property.house_rules?.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">House Rules</h2>
                <ul className="space-y-2">
                  {property.house_rules.map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-600">
                      <span className="text-slate-400">•</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Location */}
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Location</h2>
              <div className="h-64 rounded-xl overflow-hidden">
                <MapContainer
                  center={[property.lat, property.lng]}
                  zoom={14}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[property.lat, property.lng]} />
                </MapContainer>
              </div>
              <p className="mt-3 text-slate-600 text-sm">
                Exact location provided after booking is confirmed.
              </p>
            </div>

            {/* Reviews */}
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                Reviews {reviews.length > 0 && `(${reviews.length})`}
              </h2>
              {reviews.length === 0 ? (
                <p className="text-slate-500">No reviews yet. Be the first to stay here!</p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="border-b border-slate-100 pb-6 last:border-0">
                      <div className="flex items-start gap-4">
                        <Avatar>
                          <AvatarImage src={review.reviewer_picture} />
                          <AvatarFallback className="bg-trust-blue text-white">
                            {review.reviewer_name?.charAt(0) || 'R'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-slate-900">{review.reviewer_name}</h4>
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                              <span className="text-sm font-medium">{review.rating}</span>
                            </div>
                          </div>
                          <p className="text-slate-600 text-sm">{review.comment}</p>
                          <p className="text-slate-400 text-xs mt-2">
                            {format(new Date(review.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-6 shadow-lg">
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-heading text-3xl font-bold text-slate-900">
                  ${property.price_per_month.toLocaleString()}
                </span>
                <span className="text-slate-500">/month</span>
              </div>

              {property.deposit_amount > 0 && (
                <p className="text-sm text-slate-600 mb-4">
                  + ${property.deposit_amount.toLocaleString()} security deposit
                </p>
              )}

              {/* Owner Info */}
              <Link to={`/profile/${property.owner_id}`} className="flex items-center gap-3 p-3 bg-soft-gray rounded-xl mb-6 hover:bg-slate-100 transition-colors">
                <Avatar>
                  <AvatarImage src={property.owner_picture} />
                  <AvatarFallback className="bg-trust-blue text-white">
                    {property.owner_name?.charAt(0) || 'O'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{property.owner_name}</p>
                  <p className="text-sm text-slate-500">Property Owner</p>
                </div>
                <Shield className="w-5 h-5 text-green-500" />
              </Link>

              {/* Actions */}
              {user?.user_id !== property.owner_id && (
                <div className="space-y-3">
                  <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-12 font-semibold"
                        data-testid="request-booking"
                      >
                        {property.instant_booking ? 'Book Now' : 'Request to Book'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {property.instant_booking ? 'Confirm Booking' : 'Request Booking'}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block">Move-in</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start" data-testid="start-date">
                                  {startDate ? format(startDate, 'MMM d, yyyy') : 'Select date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <CalendarComponent
                                  mode="single"
                                  selected={startDate}
                                  onSelect={setStartDate}
                                  disabled={(date) => date < new Date()}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block">Move-out</label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start" data-testid="end-date">
                                  {endDate ? format(endDate, 'MMM d, yyyy') : 'Select date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <CalendarComponent
                                  mode="single"
                                  selected={endDate}
                                  onSelect={setEndDate}
                                  disabled={(date) => !startDate || date <= startDate}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-2 block">Guests</label>
                          <select
                            value={guests}
                            onChange={(e) => setGuests(parseInt(e.target.value))}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2"
                          >
                            {[...Array(property.max_guests)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>{i + 1} guest{i > 0 ? 's' : ''}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-2 block">Message to owner (optional)</label>
                          <Textarea
                            placeholder="Introduce yourself and share why you're interested..."
                            value={bookingMessage}
                            onChange={(e) => setBookingMessage(e.target.value)}
                            rows={3}
                          />
                        </div>

                        {startDate && endDate && (
                          <div className="border-t border-slate-200 pt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">
                                Rent ({differenceInDays(endDate, startDate)} days)
                              </span>
                              <span>${((property.price_per_month / 30) * differenceInDays(endDate, startDate)).toFixed(0)}</span>
                            </div>
                            {property.deposit_amount > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Security deposit</span>
                                <span>${property.deposit_amount}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-semibold pt-2 border-t">
                              <span>Total</span>
                              <span>${calculateTotal().toFixed(0)}</span>
                            </div>
                          </div>
                        )}

                        <Button
                          onClick={handleBooking}
                          disabled={submitting || !startDate || !endDate}
                          className="w-full bg-coral hover:bg-coral-hover text-white rounded-full"
                          data-testid="confirm-booking"
                        >
                          {submitting ? 'Processing...' : property.instant_booking ? 'Confirm & Pay' : 'Send Request'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full rounded-full h-12" data-testid="message-owner">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Message Owner
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Message {property.owner_name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <Textarea
                          placeholder="Hi! I'm interested in your property..."
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          rows={4}
                          data-testid="message-content"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={submitting || !messageContent.trim()}
                          className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full"
                          data-testid="send-message"
                        >
                          {submitting ? 'Sending...' : 'Send Message'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Report Listing Dialog */}
                  <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="w-full text-slate-500 hover:text-red-600 hover:bg-red-50 mt-2"
                        data-testid="report-listing-btn"
                      >
                        <Flag className="w-4 h-4 mr-2" />
                        Report This Listing
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                          Report Suspected Agency
                        </DialogTitle>
                        <DialogDescription>
                          Stay Pair Homes is for direct property owners only. Help us maintain a trusted community by reporting suspected agencies.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label className="mb-2 block">Reason for report *</Label>
                          <Select value={reportReason} onValueChange={setReportReason}>
                            <SelectTrigger data-testid="report-reason-select">
                              <SelectValue placeholder="Select a reason" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="property_manager">Property Manager / Not the Owner</SelectItem>
                              <SelectItem value="agency">Real Estate Agency</SelectItem>
                              <SelectItem value="multiple_properties">Suspiciously Many Listings</SelectItem>
                              <SelectItem value="business_account">Business/Commercial Account</SelectItem>
                              <SelectItem value="other">Other Concern</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="mb-2 block">Additional details (optional)</Label>
                          <Textarea
                            placeholder="Please share any details that led you to believe this is not a direct property owner..."
                            value={reportDetails}
                            onChange={(e) => setReportDetails(e.target.value)}
                            rows={3}
                            data-testid="report-details"
                          />
                        </div>

                        <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                          <p className="font-medium mb-1">What happens next?</p>
                          <p>Our team reviews all reports within 48 hours. If violations are found, the listing will be removed and the account suspended.</p>
                        </div>

                        <Button
                          onClick={handleReportListing}
                          disabled={reportSubmitting || !reportReason}
                          className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full"
                          data-testid="submit-report-btn"
                        >
                          {reportSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            'Submit Report'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              <p className="text-xs text-slate-400 text-center mt-4">
                You won't be charged yet
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PropertyDetailPage;
